'use client'

import { shouldShowMecFeeDisclosure } from '@/helpers/certificate-emission-type'
import { paymentInstallments } from '@/helpers/payment-installments'
import { priceFormat } from '@/helpers/price-format'
import type { UnityType } from '@/types/unity-type'
import type { WorkloadType } from '@/types/workload-type'
import Image from 'next/image'
import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'

interface CertificateProps {
  unity: UnityType
  amount: number | string | null
  isFreeCourse: boolean
  workloads?: WorkloadType[]
}

export default function Certificate({
  unity,
  amount,
  isFreeCourse,
  workloads,
}: CertificateProps) {
  const certificateFront = unity.details.metadata.certificate?.front
  const certificateBack = unity.details.metadata.certificate?.back

  const parsedAmount = Number(amount ?? 0)
  const paymentInstallmentsValues = paymentInstallments(parsedAmount)
  const shouldShowCertificatePaymentInfo = isFreeCourse

  const shouldShowMecFeeInfo = shouldShowMecFeeDisclosure({
    hasMec: unity.details?.metadata?.has_mec,
    workloads,
  })

  return (
    <div className="mt-6 md:mt-9">
      <div className="space-y-12">
        <p className="font-medium text-[#877E94] text-sm md:text-base">
          Os alunos aprovados estarão aptos à emissão do Certificado Digital em
          seu ambiente virtual, após a nota mínima atingida.
        </p>

        {shouldShowCertificatePaymentInfo && (
          <p className="font-medium text-[#877E94] text-sm md:text-base">
            A inscrição e acesso ao material deste curso serão disponibilizados
            de forma totalmente gratuita. No entanto, caso queira emitir o
            Certificado Digital de Conclusão deste curso, após o período de
            estudos exigido, será necessário efetuar o pagamento da taxa de
            emissão no valor de {priceFormat(parsedAmount * 2)}. No checkout,
            este certificado já será exibido com a condição promocional de 50%
            de desconto aplicada automaticamente, ficando por{' '}
            <strong>{priceFormat(parsedAmount)}</strong>, desde que adquirido
            junto com o E-book Guia de Carreiras 2026. O pagamento poderá ser
            feito por Boleto Bancário, Pix ou em até{' '}
            <strong>
              {paymentInstallmentsValues.quantity}x de{' '}
              {priceFormat(paymentInstallmentsValues.value)}
            </strong>{' '}
            no Cartão de Crédito. Essa promoção também será aplicada
            automaticamente aos demais certificados digitais adicionados ao
            carrinho que estiverem elegíveis à mesma condição. Caso não queira
            adquirir o e-book junto com os certificados, haverá uma opção no
            checkout para retirar a promoção; nesse caso, cada certificado
            digital ficará pelo seu respectivo valor integral, sem aplicação do
            desconto de 50%. O e-book será enviado exclusivamente em formato
            digital para o e-mail informado no momento da compra.
          </p>
        )}

        {shouldShowMecFeeInfo && (
          <p className="font-medium text-[#877E94] text-sm md:text-base">
            Caso o aluno precise que o certificado seja emitido por instituição
            credenciada ao MEC, é necessário o pagamento de uma taxa adicional
            de R$ 9,90. Com esse pagamento, o certificado será emitido pela
            Faculdade FAMEC. Caso contrário, o certificado será emitido pela
            INCI Brasil.
          </p>
        )}

        <p className="font-medium text-[#877E94] text-sm md:text-base">
          Utilizamos 8 horas diárias de estudos como base de cálculo para
          integralização da carga horaria deste curso. Após concluir o prazo
          estabelecido pelo sistema, seu certificado com datas será
          disponibilizado em sua área de aluno. Caso seu objetivo seja algum
          processo seletivo, recomendamos que analise bem o edital para
          verificar se esta modalidade de curso (Livre) atende as exigências.
        </p>

        <p className="font-medium text-[#877E94] text-sm md:text-base">
          CERTIFICADO VÁLIDO PARA: Prova de títulos em Concursos (Mediante
          Verificação do Edital), Atividade extracurricular, Apresentar na
          Faculdade, Progressão Funcional, Ampliar o seu currículo e muito mais.
        </p>
      </div>

      <div className="mt-12 md:mt-6">
        <h2 className="font-bold text-[#24125C] text-lg md:text-2xl">
          COMO FUNCIONA A PROVA?
        </h2>

        <p className="mt-2 font-medium text-[#877E94] text-sm md:text-base">
          Será disponibilizada uma prova única referente a todo o conteúdo do
          curso com 10 questões objetivas. A nota final deve ser igual ou
          superior a 6 para estar apto a emissão do certificado digital de
          conclusão do curso. Em caso de reprovação, o aluno poderá realizar
          novamente a prova quantas vezes desejar.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-6 md:mt-8">
          <div className="text-center">
            <h4 className="font-bold text-[#24125C] text-lg md:text-2xl">
              Frente:
            </h4>
            <div className="mt-4 flex items-center rounded-2xl bg-[#F4F0FE] px-5 py-4">
              <Zoom>
                <Image
                  alt="Certificado"
                  height={206.595}
                  src={certificateFront}
                  width={294}
                />
              </Zoom>
            </div>
          </div>
          <div className="text-center">
            <h4 className="font-bold text-[#24125C] text-lg md:text-2xl">
              Verso
            </h4>

            <div className="mt-4 flex items-center rounded-2xl bg-[#F4F0FE] px-5 py-4">
              <Zoom>
                <Image
                  alt="Certificado"
                  height={206.595}
                  src={certificateBack}
                  width={294}
                />
              </Zoom>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
